// The screen behind an invitation link (#/invite/<token>). It looks at the invitation first (what you would join,
// as what, who asked, until when) and only joins when you press the one primary button. Expired, used, invalid,
// signed in with an API key and "sent to another address" each get their own plain explanation. The token
// itself never shows on screen and is gone from the address bar before the first render of this component.
import { useCallback, useEffect, useRef, useState } from "react";
import type { Api } from "../api.ts";
import { createActionLock, createRequestGate } from "../asyncGuards.ts";
import {
  acceptInvite,
  expiresIn,
  type InvitePreview,
  type InviteView,
  inviterLine,
  isTerminal,
  loadInvite,
  pendingInvite,
  ROLE_BLURB,
  ROLE_NAME,
} from "../invite.ts";
import { explainError } from "./app.ts";
import { ErrorRow } from "./ErrorRow.tsx";

/** The first view: nothing to look at without a token, nothing to ask with an API key. */
export function firstView(token: string | null, apiKeySession: boolean): InviteView {
  if (!token) return { s: "none" };
  if (apiKeySession) return { s: "apikey" };
  return { s: "loading" };
}

const fullDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

export function InviteAccept({
  api,
  apiKeySession,
  hasWorkspaces,
  onJoined,
  onSignOut,
  onClose,
}: {
  api: Api;
  /** signed in with an API key, which the server refuses for invitations */
  apiKeySession: boolean;
  /** the user is already in some workspace, so "Not now" has somewhere to go */
  hasWorkspaces: boolean;
  /** the invitation was accepted: go into this workspace */
  onJoined: (workspaceId: string) => void;
  onSignOut: () => void;
  /** leave the screen without joining */
  onClose: () => void;
}) {
  // The token is read once; it lives in memory and in this tab's sessionStorage, never in the address.
  const token = useRef(pendingInvite.get());
  const [view, setView] = useState<InviteView>(() => firstView(token.current, apiKeySession));
  const [attempt, setAttempt] = useState(0);
  const gate = useRef(createRequestGate());
  const lock = useRef(createActionLock());

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` is the explicit retry
  useEffect(() => {
    const t = token.current;
    if (!t || apiKeySession) return;
    const current = gate.current.begin();
    setView({ s: "loading" });
    void loadInvite(api, t).then((v) => {
      if (!current() || pendingInvite.get() !== t) return;
      if (isTerminal(v)) pendingInvite.set(null);
      setView(v);
    });
    return () => gate.current.invalidate();
  }, [api, apiKeySession, attempt]);

  const join = useCallback(
    async (p: InvitePreview) => {
      const t = token.current;
      if (!t || !lock.current.acquire()) return;
      const current = gate.current.begin();
      setView({ s: "joining", p });
      try {
        const r = await acceptInvite(api, t, p);
        if (!current() || pendingInvite.get() !== t) return;
        if (r.ok) {
          pendingInvite.set(null);
          token.current = null;
          onJoined(r.joined.workspace_id);
          return;
        }
        if (isTerminal(r.view)) pendingInvite.set(null);
        setView(r.view);
      } finally {
        lock.current.release();
      }
    },
    [api, onJoined],
  );

  const leave = () => {
    pendingInvite.set(null);
    onClose();
  };

  return (
    <main className="invite" id="main" data-testid="invite-page" aria-labelledby="invite-title">
      <section className="invite-card" aria-live="polite">
        <p className="invite-eyebrow">Invitation</p>
        <Body
          view={view}
          hasWorkspaces={hasWorkspaces}
          onJoin={(p) => void join(p)}
          onRetry={() => setAttempt((n) => n + 1)}
          onSignOut={onSignOut}
          onLeave={leave}
        />
      </section>
    </main>
  );
}

function Title({ children }: { children: string }) {
  return (
    <h1 id="invite-title" className="invite-title">
      {children}
    </h1>
  );
}

function Why({ children }: { children: string }) {
  return <p className="invite-why">{children}</p>;
}

function Actions({ children }: { children: React.ReactNode }) {
  return <div className="invite-actions">{children}</div>;
}

function Body({
  view,
  hasWorkspaces,
  onJoin,
  onRetry,
  onSignOut,
  onLeave,
}: {
  view: InviteView;
  hasWorkspaces: boolean;
  onJoin: (p: InvitePreview) => void;
  onRetry: () => void;
  onSignOut: () => void;
  onLeave: () => void;
}) {
  const leaveBtn = (
    <button type="button" className="btn ghost" onClick={onLeave}>
      {hasWorkspaces ? "Back to the studio" : "Close"}
    </button>
  );
  switch (view.s) {
    case "loading":
      return (
        <div role="status" aria-busy="true" className="invite-loading">
          <h1 id="invite-title" className="sr-only">
            Loading the invitation
          </h1>
          <div className="sk-line" style={{ width: "60%", height: 20 }} />
          <div className="sk-line" style={{ width: "85%" }} />
          <div className="sk-line" style={{ width: "40%" }} />
        </div>
      );
    case "ready":
    case "joining": {
      const { p } = view;
      const joining = view.s === "joining";
      return (
        <>
          <Title>{`Join ${p.workspace_name}`}</Title>
          <p className="invite-lead">
            {inviterLine(p)} as <strong>{ROLE_NAME[p.role]}</strong>. {ROLE_BLURB[p.role]}.
          </p>
          <dl className="invite-facts">
            <div>
              <dt>Workspace</dt>
              <dd>{p.workspace_name}</dd>
            </div>
            <div>
              <dt>Your role</dt>
              <dd>{ROLE_NAME[p.role]}</dd>
            </div>
            <div>
              <dt>Valid until</dt>
              <dd>
                {fullDate(p.expires_at)} <span className="muted">({expiresIn(p.expires_at)})</span>
              </dd>
            </div>
          </dl>
          {p.already_member && (
            <p className="invite-note">
              You are already in this workspace. Joining only changes your role if this one is higher.
            </p>
          )}
          <Actions>
            <button type="button" className="btn primary" disabled={joining} onClick={() => onJoin(p)}>
              {joining ? "Joining…" : `Join ${p.workspace_name}`}
            </button>
            {leaveBtn}
          </Actions>
        </>
      );
    }
    case "used":
      return (
        <>
          <Title>This invitation was already used</Title>
          <Why>An invitation works once. Ask whoever invited you to send a new one.</Why>
          <Actions>{leaveBtn}</Actions>
        </>
      );
    case "expired":
      return (
        <>
          <Title>This invitation has expired</Title>
          <Why>Invitations are valid for a limited time. Ask whoever invited you to send a new one.</Why>
          <Actions>{leaveBtn}</Actions>
        </>
      );
    case "invalid":
      return (
        <>
          <Title>This invitation link does not work</Title>
          <Why>
            The link may be cut off or mistyped. Open the whole link from the message you were sent, or ask
            for a new one.
          </Why>
          <Actions>{leaveBtn}</Actions>
        </>
      );
    case "mismatch":
      return (
        <>
          <Title>{`This invitation is for another address`}</Title>
          <Why>
            It was sent to a different email address than the one you are signed in with. Sign out, then sign
            in with the invited address.
          </Why>
          <Actions>
            <button type="button" className="btn primary" onClick={onSignOut}>
              Sign out
            </button>
            {leaveBtn}
          </Actions>
        </>
      );
    case "apikey":
      return (
        <>
          <Title>Invitations need an email sign-in</Title>
          <Why>
            You are signed in with an API key. A key cannot join a workspace through an invitation. Sign out,
            then sign in with your email and open the invitation link again.
          </Why>
          <Actions>
            <button type="button" className="btn primary" onClick={onSignOut}>
              Sign out
            </button>
            {leaveBtn}
          </Actions>
        </>
      );
    case "error": {
      const why = explainError(view.message, "Something went wrong");
      return (
        <>
          <Title>The invitation could not be checked</Title>
          <ErrorRow
            title={why.title}
            detail={why.detail}
            onRetry={view.p ? () => onJoin(view.p!) : onRetry}
          />
          <Actions>{leaveBtn}</Actions>
        </>
      );
    }
    case "none":
      return (
        <>
          <Title>There is no invitation to open</Title>
          <Why>
            Open the invitation link from your message again. The code in it is removed from the address bar
            once read, to keep it private.
          </Why>
          <Actions>{leaveBtn}</Actions>
        </>
      );
  }
}
